import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  HandHeart, CheckCircle2, ArrowLeft, Phone, Mail, ShieldCheck,
} from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";

const URGENCIES = [
  { value: "low", label: "Low — general support" },
  { value: "medium", label: "Medium — within the week" },
  { value: "high", label: "High — within a day or two" },
  { value: "critical", label: "Critical — safety / immediate need" },
];

export default function PublicRefer() {
  const [form, setForm] = useState({
    referrer_name: "",
    referrer_email: "",
    referrer_phone: "",
    referrer_organization: "",
    referrer_role: "",
    seeker_first_name: "",
    seeker_last_name: "",
    seeker_preferred_name: "",
    seeker_phone: "",
    seeker_email: "",
    seeker_county: "",
    seeker_city: "",
    primary_need: "",
    notes: "",
    urgency: "medium",
    consent_acknowledged: false,
    honeypot: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.consent_acknowledged) {
      setError("Please confirm you have permission to share this referral.");
      return;
    }
    if (
      !form.referrer_name.trim() ||
      !form.referrer_email.trim() ||
      !form.seeker_first_name.trim() ||
      !form.seeker_last_name.trim()
    ) {
      setError("Your name, email, and the Care Seeker's first + last name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/referrals`, form);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.ok && result.id) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-start justify-center px-4 py-10">
        <div className="max-w-xl w-full bg-white rounded-2xl border border-[#E5E5E0] shadow-sm p-8 text-center" data-testid="refer-success">
          <div className="w-14 h-14 rounded-2xl bg-[#E8ECE7] text-[#166534] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <div className="hw-eyebrow text-[10px] mb-2">Referral received</div>
          <h1 className="font-display text-2xl font-semibold text-[#0F172A] mb-2">Thank you, {form.referrer_name.split(" ")[0]}</h1>
          <p className="text-sm text-[#334155] mb-4">
            We've received your referral for <strong>{form.seeker_first_name} {form.seeker_last_name}</strong>.
            A HarborWell Care Navigator will review it and reach out within one business day.
          </p>
          <p className="text-xs text-[#1E3A8A] mb-6">
            If anything is urgent, call the Community Wellness Hotline at 1 (833) 799-WELL.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => {
                setResult(null);
                setForm((f) => ({
                  ...f,
                  seeker_first_name: "",
                  seeker_last_name: "",
                  seeker_preferred_name: "",
                  seeker_phone: "",
                  seeker_email: "",
                  seeker_county: "",
                  seeker_city: "",
                  primary_need: "",
                  notes: "",
                  urgency: "medium",
                  consent_acknowledged: false,
                }));
              }}
              data-testid="refer-another"
              className="px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-sm text-[#334155] hover:bg-[#F9F8F6]"
            >
              Refer another person
            </button>
            <Link
              to="/"
              className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
            >
              Back to HarborWell
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-white border-b border-[#E5E5E0]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <HarborWellFormHeader
            eyebrow="Connect"
            size="md"
            testId="refer-brand-header"
            rightSlot={
              <Link to="/login" className="text-xs text-[#1E3A8A] font-medium inline-flex items-center gap-1" data-testid="refer-staff-login">
                Staff sign-in <ArrowLeft className="w-3 h-3 rotate-180" />
              </Link>
            }
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#E0E7FF] text-[#1E3A8A] flex items-center justify-center mx-auto mb-4">
            <HandHeart className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <div className="hw-eyebrow text-[10px] mb-2">Connecting people to care</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
            Refer a Care Seeker
          </h1>
          <p className="text-sm text-[#334155] mt-2 max-w-xl mx-auto">
            Partner agencies, advocates, and community members — share someone who needs wellness support and we'll
            take it from there.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E5E5E0] shadow-sm p-6 space-y-6" data-testid="public-refer-form">
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.honeypot}
            onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />

          <fieldset>
            <legend className="hw-eyebrow text-[10px] mb-3">About you</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your name *" testid="refer-your-name">
                <input required value={form.referrer_name} onChange={(e) => setForm({ ...form, referrer_name: e.target.value })} className="hw-input" />
              </Field>
              <Field label="Your email *" testid="refer-your-email">
                <input required type="email" value={form.referrer_email} onChange={(e) => setForm({ ...form, referrer_email: e.target.value })} className="hw-input" />
              </Field>
              <Field label="Phone (optional)" testid="refer-your-phone">
                <input type="tel" value={form.referrer_phone} onChange={(e) => setForm({ ...form, referrer_phone: e.target.value })} className="hw-input" />
              </Field>
              <Field label="Organization (optional)" testid="refer-your-org">
                <input value={form.referrer_organization} onChange={(e) => setForm({ ...form, referrer_organization: e.target.value })} placeholder="e.g., Vidant Health, Wayne County DSS, self-referral" className="hw-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Your role (optional)" testid="refer-your-role">
                  <input value={form.referrer_role} onChange={(e) => setForm({ ...form, referrer_role: e.target.value })} placeholder="e.g., Case Manager, Social Worker, Pastor, Family Member" className="hw-input" />
                </Field>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend className="hw-eyebrow text-[10px] mb-3">About the Care Seeker</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="First name *" testid="refer-seeker-first">
                <input required value={form.seeker_first_name} onChange={(e) => setForm({ ...form, seeker_first_name: e.target.value })} className="hw-input" />
              </Field>
              <Field label="Last name *" testid="refer-seeker-last">
                <input required value={form.seeker_last_name} onChange={(e) => setForm({ ...form, seeker_last_name: e.target.value })} className="hw-input" />
              </Field>
              <Field label="Preferred name (optional)" testid="refer-seeker-pref">
                <input value={form.seeker_preferred_name} onChange={(e) => setForm({ ...form, seeker_preferred_name: e.target.value })} className="hw-input" />
              </Field>
              <Field label="Phone (best way to reach)" testid="refer-seeker-phone">
                <input type="tel" value={form.seeker_phone} onChange={(e) => setForm({ ...form, seeker_phone: e.target.value })} className="hw-input" />
              </Field>
              <Field label="Email (optional)" testid="refer-seeker-email">
                <input type="email" value={form.seeker_email} onChange={(e) => setForm({ ...form, seeker_email: e.target.value })} className="hw-input" />
              </Field>
              <Field label="County" testid="refer-seeker-county">
                <input value={form.seeker_county} onChange={(e) => setForm({ ...form, seeker_county: e.target.value })} placeholder="e.g., Wayne" className="hw-input" />
              </Field>
              <Field label="City / town (optional)" testid="refer-seeker-city">
                <input value={form.seeker_city} onChange={(e) => setForm({ ...form, seeker_city: e.target.value })} className="hw-input" />
              </Field>
              <Field label="Urgency" testid="refer-urgency">
                <select value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })} className="hw-input">
                  {URGENCIES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend className="hw-eyebrow text-[10px] mb-3">What kind of help is needed?</legend>
            <div className="space-y-4">
              <Field label="Primary need" testid="refer-need">
                <input value={form.primary_need} onChange={(e) => setForm({ ...form, primary_need: e.target.value })} placeholder="e.g., housing, maternal wellness, food assistance, transportation" className="hw-input" />
              </Field>
              <Field label="More context (optional)" testid="refer-notes">
                <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything our Care Navigator should know — household, safety considerations, prior history, language, etc." className="hw-input" />
              </Field>
            </div>
          </fieldset>

          <div className="rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.consent_acknowledged}
                onChange={(e) => setForm({ ...form, consent_acknowledged: e.target.checked })}
                data-testid="refer-consent"
                className="mt-0.5 rounded"
                required
              />
              <span className="text-sm text-[#334155]">
                <strong className="text-[#0F172A]">I have permission to share this referral.</strong> Either the Care
                Seeker (or their legal advocate) has consented to HarborWell reaching out, or I am referring myself.
              </span>
            </label>
          </div>

          {error && (
            <div className="rounded-xl bg-[#FEE2E2] text-[#991B1B] p-3 text-sm" data-testid="refer-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="refer-submit"
            className="w-full py-3 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send referral"}
          </button>

          <p className="text-xs text-[#1E3A8A] text-center flex items-center justify-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.75} />
            Information shared here is kept confidential within HarborWell.
          </p>
        </form>

        <div className="grid sm:grid-cols-2 gap-3 mt-6 text-sm">
          <a href="tel:+18337799355" className="flex items-center gap-3 bg-white rounded-2xl border border-[#E5E5E0] p-4 hover:bg-[#F9F8F6]" data-testid="refer-hotline">
            <Phone className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.75} />
            <div>
              <div className="text-xs text-[#1E3A8A] uppercase tracking-wider">Community Wellness Hotline</div>
              <div className="font-medium text-[#0F172A]">1 (833) 799-WELL (9355)</div>
            </div>
          </a>
          <a href="mailto:contact@harborwell.care" className="flex items-center gap-3 bg-white rounded-2xl border border-[#E5E5E0] p-4 hover:bg-[#F9F8F6]" data-testid="refer-email">
            <Mail className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.75} />
            <div>
              <div className="text-xs text-[#1E3A8A] uppercase tracking-wider">Prefer email?</div>
              <div className="font-medium text-[#0F172A]">contact@harborwell.care</div>
            </div>
          </a>
        </div>
      </main>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider" data-testid={testid}>{label}</label>
      {children}
    </div>
  );
}
